import React, { memo, useEffect, useRef, useState } from 'react';
import { selectBetReferral, selectErrori } from '../../sportTicketSelectors';
import { selectErrorLabels, selectIsFullCart } from 'features/carrello/carrelloSelectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { BannerInformationPropsType } from 'components/bannerInformation';
import { CarrelloFooterContainer } from 'features/carrello/carrelloFooterContainer';
import { CarrelloFooterSingolaMultipla } from 'features/carrello/carrelloFooterSingolaMultipla';
import { MovimentiTipoScommessaEnum } from 'types/swagger';
import { SportTicketAvvenimento } from '../../utils/calcoloCarrello/types';
import { SportTicketItem } from '../sportTicketItem';
import { dismissChangedQuoteWarning } from '../../utils/calcoloCarrello/utils';
import { hasBetReferralTimer } from '../../sportTicketSlice';
import { selectBetReferralTiming } from 'features/configuration/selectors';
import styles from './SportTicketSingolaMultipla.module.scss';
import { venditaBetReferralReset } from '../../sportTicketActions';

export type SportTicketSingolaMultiplaProps = {
  bonus: number;
  isMultipla?: boolean;
  avvenimenti: SportTicketAvvenimento[];
  quotaTotale: number;
  possibileVincitaMin: number;
  possibileVincitaMinWithBonus: number;
};

export const SportTicketSingolaMultipla = memo(function SportTicketSingolaMultipla({
  bonus,
  isMultipla,
  avvenimenti,
  quotaTotale,
  possibileVincitaMin,
  possibileVincitaMinWithBonus,
}: SportTicketSingolaMultiplaProps) {
  const dispatch = useAppDispatch();
  const errori = useTypedSelector(selectErrori);
  const isFullCart = useTypedSelector(selectIsFullCart);
  const errorsLabels = useTypedSelector(selectErrorLabels);
  const betReferral = useTypedSelector(selectBetReferral);
  const betReferralTiming = useTypedSelector(selectBetReferralTiming);
  const [betReferralTimeRemaining, setBetReferralTimeRemaining] = useState<number>(betReferralTiming);
  const timer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (betReferral?.isActive) {
      timer.current = setInterval(() => {
        setBetReferralTimeRemaining((betReferralTimeRemaining) => betReferralTimeRemaining - 1);
      }, 1000);

      dispatch(hasBetReferralTimer(true));
      if (betReferralTimeRemaining === 0) {
        // create a thunk
        clearInterval(timer.current);
        setBetReferralTimeRemaining(betReferralTimeRemaining);
        dispatch(venditaBetReferralReset());
      }
    }
    if (!betReferral?.isActive && timer.current) {
      clearInterval(timer.current);
      setBetReferralTimeRemaining(betReferralTimeRemaining);
    }
    return () => clearInterval(timer.current!);
  }, [betReferral?.isActive, betReferralTimeRemaining, dispatch]);

  return (
    <React.Fragment>
      {isFullCart && (
        <div className={styles.wrapper}>
          {avvenimenti?.map((avvenimento) => {
            return <SportTicketItem key={`sport-ticket-${avvenimento.id}-multipla`} avvenimento={avvenimento} />;
          })}
        </div>
      )}

      <CarrelloFooterContainer
        type={MovimentiTipoScommessaEnum.Sport}
        errors={
          errori.erroriCarrello?.length > 0 && Array.isArray(errori.erroriCarrello)
            ? Array.from(errori.erroriCarrello, (errore) => {
                const foundError = errorsLabels?.find((errorLabel) => errorLabel.key === errore.type);
                return {
                  type: foundError?.tipo as BannerInformationPropsType['type'],
                  text: errore.value
                    ? foundError?.value.replace(/{{valore}}/, errore.value.toString())!
                    : foundError?.value!,
                  visible: true,
                  layoutType: 'small',
                };
              })
            : errori.erroriVendita?.length > 0 && Array.isArray(errori.erroriVendita)
            ? Array.from(errori.erroriVendita, (errore) => {
                const foundError = errorsLabels?.find((errorLabel) =>
                  typeof errore === 'string' ? errorLabel.key === errore : errorLabel.key === errore.type
                );
                return {
                  type: foundError?.tipo as BannerInformationPropsType['type'],
                  text: foundError?.value!,
                  visible: true,
                  layoutType: 'small',
                  isDismissible: true,
                  actionOnDismiss: dismissChangedQuoteWarning({
                    dispatcher: dispatch,
                    error: foundError!,
                  }),
                };
              })
            : errori.erroriBetReferral?.length > 0 && Array.isArray(errori.erroriBetReferral)
            ? Array.from(errori.erroriBetReferral, (errore) => {
                const foundError = errorsLabels?.find((errorLabel) =>
                  typeof errore === 'string' ? errorLabel.key === errore : errorLabel.key === errore.type
                );
                return {
                  type: foundError?.tipo as BannerInformationPropsType['type'],
                  text: `${betReferral?.descrizione}. Scommetti entro i prossimi ${betReferralTimeRemaining} secondi`,
                  visible: true,
                  layoutType: 'small',
                  isDismissible: true,
                  actionOnDismiss: dismissChangedQuoteWarning({
                    dispatcher: dispatch,
                    error: foundError!,
                  }),
                };
              })
            : undefined
        }
      >
        <CarrelloFooterSingolaMultipla
          bonus={bonus}
          isMultipla={isMultipla}
          quotaTotale={quotaTotale}
          possibileVincita={possibileVincitaMin}
          possibileVincitaconBonus={possibileVincitaMinWithBonus}
          type={MovimentiTipoScommessaEnum.Sport}
          hasSettings
        />
      </CarrelloFooterContainer>
    </React.Fragment>
  );
});
